import React from 'react';
import { pack } from 'msgpackr';

// Only the standalone end-of-exam path installs this wrapper. Recording components
// and the company invitation protocol remain untouched.
export function installFinalDrain(recorder, state, isOverall = false) {
  if (!recorder?.addEventListener) return () => {};
  const original = recorder.ondataavailable;
  // Each speaking recording gets its own recorder. Bind its question now:
  // a slow final chunk may finish after SAVED has advanced the screen.
  const initial = state();
  const finalQuestion = !isOverall && initial.currentQuestion === initial.totalQuestions &&
    initial.questions?.[initial.currentQuestion - 1]?.questionType === 'speaking'
    ? initial.questions[initial.currentQuestion - 1].qID : null;
  const pending = new Set();
  let failed = false;
  const wrapped = function (event) {
    const promise = Promise.resolve().then(() => original?.call(recorder, event));
    pending.add(promise);
    promise.catch(() => { failed = true; }).finally(() => pending.delete(promise));
    return promise;
  };
  recorder.ondataavailable = wrapped;
  const stopped = async () => {
    await Promise.allSettled([...pending]);
    const current = state();
    if (finalQuestion && current.ws?.readyState === 1) {
      current.ws.send(pack({ type: failed ? 'STANDALONE_RECORDING_FAILED' : 'STANDALONE_FINAL_DRAINED', format: finalQuestion }));
    } else if (current.user?.noq?.speaking === 0 && current.ws?.readyState === 1 &&
        (current.page === 'DONE' || current.page === 'DONEREVISITED')) {
      // With no speaking section there is no speaking transition to flush ALL.
      // Wait for the last chunk before asking the server to save that recording.
      current.ws.send(pack({ type: failed ? 'STANDALONE_RECORDING_FAILED' : 'REC_FINISHED', format: 'ALL' }));
    }
  };
  recorder.addEventListener('stop', stopped);
  return () => {
    if (recorder.ondataavailable === wrapped) recorder.ondataavailable = original;
    recorder.removeEventListener('stop', stopped);
  };
}

export function useStandaloneCompletion(read, liveState) {
  const [message, setMessage] = React.useState('Finishing your submission… / Sınavınız kaydediliyor…');
  const standalone = read.user?.standalone;
  const enabled = Boolean(standalone);
  const session = React.useRef({ ws: null, overallRecorder: null });
  React.useEffect(() => {
    if (!enabled || !read.recorder?.addEventListener) return;
    if (session.current.ws !== read.ws) session.current = { ws: read.ws, overallRecorder: null };
    if (!session.current.overallRecorder) session.current.overallRecorder = read.recorder;
    // The first recorder is always the overall recording, even when the only
    // question is speaking. It must not acknowledge that answer's final chunk.
    return installFinalDrain(read.recorder, () => liveState, session.current.overallRecorder === read.recorder);
  }, [enabled, read.recorder, read.ws, liveState]);
  const terminal = read.page === 'DONE' || read.page === 'DONEREVISITED';
  React.useEffect(() => {
    if (!enabled || !terminal) return;
    let cancelled = false, timer;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const response = await fetch(standalone.completionUrl, {
          headers: { Authorization: `Bearer ${standalone.token}` }, cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) throw new Error('completion_pending');
        const result = await response.json();
        if (cancelled) return;
        if (result.status === 'ready') {
          const target = new URL(result.returnUrl);
          if (target.origin !== new URL(standalone.returnOrigin).origin || !/^\/panel-result\/[a-f0-9-]+$/i.test(target.pathname)) throw new Error('invalid_return');
          if (read.ws) { read.ws.onclose = null; read.ws.close(); }
          window.location.replace(target.href); return;
        }
        if (result.status === 'recording_failed') {
          setMessage('Your answers are saved, but a recording needs support review. Please keep your payment reference. / Yanıtlarınız kaydedildi; kayıt için destek incelemesi gerekiyor.');
        } else if (result.status === 'held' || result.status === 'expired') {
          setMessage('Please contact Global Assessment about this exam. / Bu sınav için Global Assessment ile iletişime geçin.');
          return;
        }
      } catch {
        if (!cancelled) setMessage('Confirming your submission. Please keep this page open; it will retry automatically. / Sınavınız doğrulanıyor. Lütfen bu sayfayı açık tutun.');
      }
      if (!cancelled) timer = setTimeout(poll, 3000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [enabled, terminal, standalone?.completionUrl, standalone?.token, standalone?.returnOrigin, read.ws]);
  return enabled ? message : null;
}
